import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ArrowDown, ArrowUp, Check, ChevronDown, CirclePlus, PenIcon, Trash2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent } from '@radix-ui/react-dropdown-menu';
import { Button } from '../../components/ui/button';
import { DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import Input from '../../components/ui/input';
import useServices from '../../stores/useServices';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import Skeleton from '../../components/ui/skeleton';

interface ServiceCardProps {
  id: number;
  title: string;
  description?: string | null;
  imageUrl: string;
  onEdit: () => void;
  onDelete: () => void;
  isSelected?: boolean;
  onSelect?: () => void;
  isSelectMode?: boolean;
}

function ServiceCard({
  id,
  title,
  description,
  imageUrl,
  onEdit,
  onDelete,
  isSelectMode,
  isSelected,
  onSelect,
}: ServiceCardProps) {
  return (
    <div
      className={`h-72 hover:drop-shadow-md bg-white border border-border rounded-lg overflow-hidden ${
        isSelected ? 'ring-2 ring-blue-500' : ''
      }`}
    >
      <Button
        variant="ghost"
        onClick={isSelectMode ? onSelect : undefined}
        className="absolute opacity-0 h-72 w-full max-w-[300px] z-10"
      >
        Select Button
      </Button>
      <img src={imageUrl} alt={title} className="h-32 object-cover object-center w-full" />
      <div className="flex flex-col gap-1 px-3 py-2">
        <div className="flex z-20 justify-between items-center">
          <h2 className="font-medium whitespace-nowrap overflow-hidden text-ellipsis">{title}</h2>
          {!isSelectMode ? (
            <div className="flex gap-2 px-2 py-1.5 items-center border border-border rounded-lg">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <Trash2 className="h-4 text-red-600" />
                <span className="sr-only">Delete</span>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <PenIcon className="h-4 text-primary" />
                <span className="sr-only">Edit</span>
              </button>
            </div>
          ) : (
            isSelected && (
              <div className="bg-blue-500 rounded-full p-1">
                <Check className="h-4 w-4 text-white" />
              </div>
            )
          )}
        </div>
        <DropdownMenuSeparator />
        <p className="text-sm line-clamp-4">{description}</p>
      </div>
    </div>
  );
}

export default function ListService() {
  const { services, removeLoading, hasFetched, fetchLoading, fetchServices, removeService } = useServices();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedService, setSelectedService] = useState<{ id: number; name: string } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [sortBy, setSortBy] = useState<'name_srv_id' | 'created_at' | 'edited_at'>(() => {
    const savedSortBy = localStorage.getItem('serviceSortBy');
    return savedSortBy ? (savedSortBy as 'name_srv_id' | 'created_at' | 'edited_at') : 'edited_at';
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    const savedSortOrder = localStorage.getItem('serviceSortOrder');
    return savedSortOrder ? (savedSortOrder as 'desc' | 'asc') : 'desc';
  });

  useEffect(() => {
    localStorage.setItem('serviceSortBy', sortBy);
    localStorage.setItem('serviceSortOrder', sortOrder);
  }, [sortBy, sortOrder]);

  useEffect(() => {
    if (!hasFetched) {
      fetchServices();
    }
  }, [fetchServices, hasFetched]);

  const handleTabClick = () => {
    setSearchParams({ tab: 'form' });
  };

  const handleEdit = (id: number) => {
    setSearchParams({ tab: 'form', edit: String(id) });
  };

  const handleDeleteClick = (id: number, name: string) => {
    setSelectedService({ id, name });
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedService) {
      await removeService(selectedService.id);
      setIsModalOpen(false);
      setSelectedService(null);
    } else if (selectedIds.length > 0) {
      const deleteWithDelay = async (ids: number[], index = 0) => {
        if (index >= ids.length) {
          setSelectedIds([]);
          setIsModalOpen(false);
          setIsSelectMode(false);
          return;
        }

        await removeService(ids[index]);
        setTimeout(() => deleteWithDelay(ids, index + 1), 200);
      };

      await deleteWithDelay(selectedIds);
    }
  };

  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    setSelectedIds([]);
  };

  const handleSelect = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]));
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length > 0) {
      setIsModalOpen(true);
    }
  };

  const handleSortChange = (field: 'name_srv_id' | 'created_at' | 'edited_at') => {
    setSortBy(field);
  };

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
  };

  const filteredServices = services
    ? services
        .filter((service) => service.name_srv_id.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort((a, b) => {
          const valueA = a[sortBy];
          const valueB = b[sortBy];

          if (sortBy === 'name_srv_id') {
            return sortOrder === 'asc'
              ? String(valueA).localeCompare(String(valueB))
              : String(valueB).localeCompare(String(valueA));
          }
          return sortOrder === 'asc'
            ? new Date(valueA as string).getTime() - new Date(valueB as string).getTime()
            : new Date(valueB as string).getTime() - new Date(valueA as string).getTime();
        })
    : [];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-2">
        <div className="flex justify-between items-center w-full">
          <h1 className="font-medium text-md">Manage Service</h1>
          <div className="flex gap-2">
            {isSelectMode && (
              <Button variant="destructive" onClick={handleDeleteSelected} disabled={selectedIds.length === 0}>
                <Trash2 size={16} />
                <span className="hidden md:inline ml-1">Delete Selected ({selectedIds.length})</span>
              </Button>
            )}
            <Button variant="outline" onClick={toggleSelectMode}>
              {isSelectMode ? 'Cancel' : 'Select'}
            </Button>
            {!isSelectMode && (
              <Button variant="default" onClick={handleTabClick}>
                <CirclePlus size={16} />
                <span className="hidden md:inline">Create New Product</span>
              </Button>
            )}
          </div>
        </div>
        <div className="flex justify-between items-center w-full md:flex-row flex-col">
          <Input
            placeholder="Search by name"
            className="max-w-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="flex justify-between md:w-fit w-full">
            <Button
              variant="ghost"
              type="button"
              className="p-2 hover:bg-neutral-50 rounded-lg"
              onClick={toggleSortOrder}
            >
              <span className="sr-only">Sort Order</span>
              {sortOrder === 'asc' ? <ArrowUp className="h-4" /> : <ArrowDown className="h-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-2 hover:bg-neutral-50 text-sm rounded-lg flex gap-1">
                  {sortBy === 'name_srv_id' && 'Nama'}
                  {sortBy === 'created_at' && 'Dibuat'}
                  {sortBy === 'edited_at' && 'Terakhir diubah'}
                  <ChevronDown className="h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-border z-50 bg-white shadow border p-1 rounded-lg">
                <DropdownMenuItem>
                  <button type="button" onClick={() => handleSortChange('name_srv_id')}>
                    Nama
                  </button>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <button type="button" onClick={() => handleSortChange('created_at')}>
                    Dibuat
                  </button>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <button type="button" onClick={() => handleSortChange('edited_at')}>
                    Terakhir diubah
                  </button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      {fetchLoading ? (
        <section className="grid md:grid-cols-[repeat(auto-fill,_minmax(300px,_1fr))] grid-cols-[repeat(auto-fill,_minmax(200px,_1fr))] gap-4 w-full">
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
        </section>
      ) : (
        <main className="grid md:grid-cols-[repeat(auto-fill,_minmax(300px,_1fr))] grid-cols-[repeat(auto-fill,_minmax(200px,_1fr))] gap-2 w-full">
          {filteredServices.length > 0 ? (
            filteredServices.map((service) => (
              <ServiceCard
                key={service.service_id}
                id={service.service_id}
                title={service.name_srv_id}
                description={service.description_srv_id}
                imageUrl={service.image_srv || '/assets/no-data-image.svg'}
                onEdit={() => handleEdit(service.service_id)}
                onDelete={() => handleDeleteClick(service.service_id, service.name_srv_id)}
                isSelectMode={isSelectMode}
                isSelected={selectedIds.includes(service.service_id)}
                onSelect={() => handleSelect(service.service_id)}
              />
            ))
          ) : (
            <p className="px-3 py-2 col-span-full text-center text-neutral-300">No data</p>
          )}
          <ConfirmDeleteModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={handleConfirmDelete}
            itemName={selectedService ? selectedService?.name : `${selectedIds.length} selected items`}
            loading={removeLoading}
          />
        </main>
      )}
    </div>
  );
}
